import { Box } from "@rebass/grid";
import { EIntakeStep } from "@rentiohq/shared-frontend/dist/types/application.types";
import { IUser } from "@rentiohq/shared-frontend/dist/types/auth.types";
import { IBroker } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { ELocale } from "@rentiohq/shared-frontend/dist/types/i18n.types";
import { IProperty } from "@rentiohq/shared-frontend/dist/types/property.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  ESpacings,
  MultiStepForm,
  SegmentedControl,
} from "@rentiohq/web-shared/dist/components";
import * as systemSelectors from "@rentiohq/web-shared/dist/redux/system/system.selectors";
import React from "react";
import { useSelector } from "react-redux";
import { IRootStore } from "redux/reducers";
import * as CONFIG from "scenes/Settings/scenes/TenantProfileNotificationPreferences/TenantProfileNotificationPreferences.config";
import {
  CANDIDACY_DEADLINE_FIELD,
  toCandidacyDeadlinePayload,
} from "utils/candidacyDeadline.utils";
import propertyApplicationNotEligible from "../../../../../../forms/propertyApplicationNotEligible";
import { getTenantFeedbackTextPerApplication } from "../../../../../../forms/propertyApplicationNotEligible/schema.propertyApplicationNotEligible.utils";
import * as t from "../../../../../../services/translationService";
import { IStatusChange } from "../../PropertyDetailApplicationsContainer";
import { ICustomFeedbackParams } from "./customFeedbackModal.types";
import {
  getNotEligibleLocaleMessages,
  IPreference,
  NOT_ELIGIBLE_TEXTS,
  preferences,
} from "./customFeedbackModal.utils";

interface IProps {
  property: IProperty;
  user: IUser;
  broker?: IBroker;
  formId: number;
  customFeedbackParams: ICustomFeedbackParams;
  isLoadingInvite: boolean;
  handleChange: (statusChanges: IStatusChange[]) => void;
  setCustomFeedbackParams: (
    customFeedbackParams?: ICustomFeedbackParams,
  ) => void;
}

const getLocalizedMessages = (
  customFeedbackParams: ICustomFeedbackParams,
  notificationPreferences: IPreference[],
  notEligibleTextIndex = 0,
) => {
  let localeMessages = {};

  const getPreferenceByKey = (key: string) =>
    notificationPreferences.find(x => x.key === key);

  const statusChange = customFeedbackParams?.statusChanges[0];

  if (statusChange.step === EIntakeStep.visitor) {
    localeMessages = {
      [ELocale.NL]: getPreferenceByKey(CONFIG.PREFERENCE_VISITOR_NL)?.value,
      [ELocale.FR]: getPreferenceByKey(CONFIG.PREFERENCE_VISITOR_FR)?.value,
      [ELocale.EN]: getPreferenceByKey(CONFIG.PREFERENCE_VISITOR_EN)?.value,
    };
  } else if (statusChange.step === EIntakeStep.waitingList) {
    localeMessages = {
      [ELocale.NL]: getPreferenceByKey(CONFIG.PREFERENCE_WAITING_LIST_NL)
        ?.value,
      [ELocale.FR]: getPreferenceByKey(CONFIG.PREFERENCE_WAITING_LIST_FR)
        ?.value,
      [ELocale.EN]: getPreferenceByKey(CONFIG.PREFERENCE_WAITING_LIST_EN)
        ?.value,
    };
  } else if (statusChange.step === EIntakeStep.planAsVisitor) {
    localeMessages = {
      [ELocale.NL]: getPreferenceByKey(CONFIG.PREFERENCE_PLAN_FOR_VISIT_NL)
        ?.value,
      [ELocale.FR]: getPreferenceByKey(CONFIG.PREFERENCE_PLAN_FOR_VISIT_FR)
        ?.value,
      [ELocale.EN]: getPreferenceByKey(CONFIG.PREFERENCE_PLAN_FOR_VISIT_EN)
        ?.value,
    };
  } else if (statusChange.step === EIntakeStep.invited) {
    localeMessages = {
      [ELocale.NL]: getPreferenceByKey(CONFIG.PREFERENCE_INVITED_NL)?.value,
      [ELocale.FR]: getPreferenceByKey(CONFIG.PREFERENCE_INVITED_FR)?.value,
      [ELocale.EN]: getPreferenceByKey(CONFIG.PREFERENCE_INVITED_EN)?.value,
    };
  } else if (statusChange.step === EIntakeStep.candidateTenant) {
    localeMessages = {
      [ELocale.NL]: getPreferenceByKey(CONFIG.PREFERENCE_CANDIDATE_TENANT_NL)
        ?.value,
      [ELocale.FR]: getPreferenceByKey(CONFIG.PREFERENCE_CANDIDATE_TENANT_FR)
        ?.value,
      [ELocale.EN]: getPreferenceByKey(CONFIG.PREFERENCE_CANDIDATE_TENANT_EN)
        ?.value,
    };
  } else if (statusChange.step === EIntakeStep.notEligible) {
    localeMessages = getNotEligibleLocaleMessages(
      key => getPreferenceByKey(key)?.value,
      notEligibleTextIndex,
    );
  }

  return localeMessages;
};

export const CustomFeedbackModal = ({
  property,
  user,
  broker,
  formId,
  customFeedbackParams,
  isLoadingInvite,
  handleChange,
  setCustomFeedbackParams,
}: IProps) => {
  const notificationPreferences = useSelector((state: IRootStore) =>
    systemSelectors.getPreferences(state, preferences),
  );
  const { statusChanges, applications } = customFeedbackParams;

  // Which of the two rejection texts this send uses. Nothing here is written
  // back to the settings: the boxes stay editable for this send only
  // (Jonas, 2026-10-05).
  const [notEligibleTextIndex, setNotEligibleTextIndex] = React.useState(0);

  const isNotEligible = statusChanges[0]?.step === EIntakeStep.notEligible;

  const getTextName = (index: number) => {
    const text = NOT_ELIGIBLE_TEXTS[index];
    const stored = notificationPreferences.find(x => x.key === text.nameKey)
      ?.value;

    return `${stored || ""}`.trim() || getLocalizedText(text.defaultNameKey);
  };

  // This mail is never optional: a broker may rewrite it but not stop it, so
  // the modal offers no way past it and says so in its own words rather than
  // asking whether to notify at all (DEV-3317).
  const isMailAlwaysSent =
    statusChanges[0]?.step === EIntakeStep.candidateTenant;

  // Built once and handed to both the form and the send, so what the broker was
  // shown and what each candidate is sent are worked out from the same inputs.
  const extraData = {
    property,
    // The step decides whether the text must keep a link placeholder.
    step: statusChanges[0].step,
    applications,
    user,
    broker,
    localeMessages: getLocalizedMessages(
      customFeedbackParams,
      notificationPreferences,
      notEligibleTextIndex,
    ),
    // The invitation to apply asks for the candidacy by a date (DEV-3531).
    withCandidacyDeadline: isMailAlwaysSent,
  };

  return (
    <MultiStepForm
      // Remounted when the broker switches text, because MultiStepForm calls
      // createForm in its constructor only: a new formId on a mounted form
      // creates no state for that id, and the modal rendered no boxes at all
      // (Jonas on stag, DEV-3325). The remount is also what keeps each text's
      // own edits, since each id holds its own form state.
      key={`application-mail-not_eligible-${formId}-text-${notEligibleTextIndex}`}
      preventSubmitThroughEnter={true}
      // One form state per text, so a broker who edits the first, looks at
      // the second and comes back finds their own words rather than the
      // stored text again (Jonas, 2026-10-05).
      formId={`application-mail-not_eligible-${formId}-text-${notEligibleTextIndex}`}
      schemas={propertyApplicationNotEligible(extraData)}
      asModal={true}
      withAside={false}
      onSuccess={(input: { [field: string]: unknown }) => {
        // One box per language in the selection, so each candidate is sent the
        // text of their own, edited by the broker or not.
        const textPerApplication = getTenantFeedbackTextPerApplication(
          extraData,
          input,
        );

        // One deadline for the whole selection; null when the broker cleared
        // the field. Left out entirely on every other step.
        const candidacyDeadline = extraData.withCandidacyDeadline
          ? toCandidacyDeadlinePayload(input[CANDIDACY_DEADLINE_FIELD])
          : undefined;

        handleChange(
          statusChanges.map(statusChange => ({
            ...statusChange,
            tenantFeedbackText: textPerApplication[statusChange.id],
            candidacyDeadline,
          })),
        );
        setCustomFeedbackParams(undefined);
      }}
      submitLabel={t.system("submit")}
      modalActions={
        isMailAlwaysSent
          ? []
          : [
              {
                content: getLocalizedText(
                  "property.application.not_eligible.info_modal.cta.continue_without_email",
                ),
                onClick: () => {
                  handleChange(statusChanges);
                  setCustomFeedbackParams(undefined);
                },
                appearance: "outline",
              },
            ]
      }
      modalProps={{
        onClose: () => setCustomFeedbackParams(undefined),
        width: "medium",
        children: (
          <Box mb={ESpacings.loose}>
            {getLocalizedText(
              isMailAlwaysSent
                ? "property.application.candidate_tenant.info_modal.info"
                : "property.application.not_eligible.info_modal.info",
            )}

            {/* The office's two rejection texts, by the names it gave them.
                A switch rather than a dropdown, so both names are readable
                without opening anything (Jonas, 2026-10-05). */}
            {isNotEligible && (
              <Box mt={ESpacings.base}>
                <SegmentedControl
                  name="not-eligible-text"
                  options={NOT_ELIGIBLE_TEXTS.map((_text, index: number) => ({
                    id: index,
                    value: getTextName(index),
                  }))}
                  selected={notEligibleTextIndex}
                  onChange={(value: number) => setNotEligibleTextIndex(value)}
                />
              </Box>
            )}
          </Box>
        ),
      }}
      isLoading={isLoadingInvite}
    />
  );
};
